import { DataAccess } from "../dataaccess/data.access";
import { ProfileUtil } from "../modules/s25-datepattern/profile.util";
import { EventFormOccurrenceUtil } from "../modules/s25-event-creation-form/occurrences/event.form.occurrence.util";
import { Event } from "../pojo/Event";
import { TimeModelI } from "../pojo/TimeModelI";
import { S25Util } from "../util/s25-util";
import { Cache } from "../decorators/cache.decorator";
import { Proto } from "../pojo/Proto";
import NumericalBoolean = Proto.NumericalBoolean;
import { Timeout } from "../decorators/timeout.decorator";

export interface StandardSchedule {
    id?: number;
    start_time?: number;
    end_time?: number;
    weekskip?: NumericalBoolean;
    dow?: string;
    pre_range?: NumericalBoolean;
    post_range?: NumericalBoolean;
    schedule_id?: number;
    schedule_name?: string;
}

export interface StandardScheduleList {
    id: number;
    name: string;
}

export interface StandardScheduleOccurrencesResult {
    occurrences: Event.Occurrence[];
    profileCode: string;
}

export class StandardScheduleService {
    @Timeout
    @Cache({ immutable: true, targetName: "StandardScheduleService" })
    public static getStandardScheduleList(typeId: number): Promise<StandardScheduleList[]> {
        return DataAccess.get(
            DataAccess.injectCaller(
                "/standard_schedules/list.json?typeId=" + typeId,
                "StandardScheduleService.getStandardSchedulesList",
            ),
        ).then(function (data) {
            return data?.root?.item || [];
        });
    }

    @Timeout
    @Cache({ immutable: true, targetName: "StandardScheduleService" })
    public static getStandardSchedule(itemIds: number | number[]) {
        const ids = S25Util.array.forceArray(itemIds);
        const payload = { mapxml: { itemId: ids.join(",") } };
        return DataAccess.post<{ root: StandardSchedule[] }>(
            DataAccess.injectCaller(
                "/standard_schedules/details.json?request_method=get",
                "StandardScheduleService.getStandardSchedule",
            ),
            payload,
        ).then(function (data) {
            return data?.root || [];
        });
    }

    public static formatPattern(schedule: StandardSchedule): string {
        const dayCodeMap: { [key: string]: string } = {
            U: "SU",
            M: "MO",
            T: "TU",
            W: "WE",
            R: "TH",
            F: "FR",
            S: "SA",
        };
        const endTime = S25Util.date.toTimeStr(S25Util.timeConvert(schedule.end_time!), true);
        const startTime = S25Util.date.toTimeStr(S25Util.timeConvert(schedule.start_time!), true);

        return `${startTime.trim()}|${endTime.trim()}|W${schedule.weekskip! + 1} ${schedule
            .dow!.split("")
            .map((day) => dayCodeMap[day])
            .join(" ")}`;
    }

    public static buildOccurrencesForTerm(
        pattern: StandardSchedule,
        termStart: Date,
        termEnd: Date,
    ): StandardScheduleOccurrencesResult {
        const queryCode = StandardScheduleService.formatPattern(pattern);
        const parts = queryCode.split("|");
        const profileCode = parts[2];

        const startTime = S25Util.date.parseTime(S25Util.timeConvert(pattern.start_time!));
        const endTime = S25Util.date.parseTime(S25Util.timeConvert(pattern.end_time!));

        let startDt = S25Util.date.copyHourMinute(termStart, startTime);
        let endDt = S25Util.date.copyHourMinute(termStart, endTime);
        let durationMinutes = S25Util.date.diffMinutes(startDt, endDt);
        if (durationMinutes <= 0) {
            endDt = S25Util.date.addDays(endDt, 1);
            durationMinutes = S25Util.date.diffMinutes(startDt, endDt);
        }

        const profileModel = ProfileUtil.getProfileModel(null, profileCode, termEnd, startDt);

        if (profileModel.type === "weekly") {
            const firstDowIndex = profileModel.daysOfWeek!.findIndex((dow) => dow.chosen);
            if (firstDowIndex !== -1) {
                startDt = S25Util.date.changeDow(startDt, firstDowIndex);
                endDt = S25Util.date.addMinutes(S25Util.date.clone(startDt), durationMinutes);

                const termStartDate = S25Util.date.getDate(termStart);
                if (termStartDate > S25Util.date.getDate(startDt)) {
                    const termStartDow = termStart.getDay();
                    const newDowIndex = profileModel.daysOfWeek!.findIndex(
                        (dow) => dow.chosen && Number(dow.itemId) >= termStartDow,
                    );
                    if (newDowIndex !== -1) {
                        startDt = S25Util.date.changeDow(termStart, newDowIndex);
                    } else {
                        startDt = S25Util.date.changeDow(termStart, firstDowIndex);
                        startDt = S25Util.date.addWeeks(startDt, 1);
                    }
                    endDt = S25Util.date.addMinutes(S25Util.date.clone(startDt), durationMinutes);
                }
            }
        }

        const timeModel: TimeModelI = {
            evStartDt: startDt,
            evEndDt: endDt,
            minutes: {
                setupMinutes: 0,
                preMinutes: Number(pattern.pre_range) || 0,
                postMinutes: Number(pattern.post_range) || 0,
                takedownMinutes: 0,
            },
        };

        const termEndDay = S25Util.date.toEndOfDay(termEnd);
        let occurrences = ProfileUtil.getOccurrences(timeModel, profileModel, termEnd);
        occurrences = occurrences.filter((occurrence) => EventFormOccurrenceUtil.getEndDt(occurrence) <= termEndDay);

        return {
            occurrences,
            profileCode: ProfileUtil.getProfileCodeByModel(profileModel),
        };
    }

    public static formatPatternLabel(pattern: StandardSchedule, timeFormat: string): string {
        const startTime = S25Util.date.parseTime(S25Util.timeConvert(pattern.start_time!));
        const endTime = S25Util.date.parseTime(S25Util.timeConvert(pattern.end_time!));
        const startLabel = S25Util.date.printTime(
            { day: 0, hour: startTime.getHours(), minute: startTime.getMinutes() },
            timeFormat,
        );
        const endLabel = S25Util.date.printTime(
            { day: 0, hour: endTime.getHours(), minute: endTime.getMinutes() },
            timeFormat,
        );
        return `${pattern.dow} (${startLabel} – ${endLabel})`;
    }
}
